import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FullCalendarComponent } from '@fullcalendar/angular';
import {
  CalendarOptions,
  DatesSetArg,
  EventClickArg,
} from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { DateTime } from 'luxon';
import { CalendarStore } from '../calendar.store';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { HiTextStore } from '@calendar/hi-text/hi-text.store';
import { MonthlyTotalsInfoDialogComponent } from './monthly-totals-info-dialog/monthly-totals-info-dialog.component';
import { ActivityDescriptionDialogComponent } from './activity-description-dialog/activity-description-dialog.component';
import { REMOVE_CODES } from '@shared/misc/remove-codes';
import { SabreTextDialogComponent } from '@shared/components/sabre-text-dialog/sabre-text-dialog.component';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { stripTime } from '@shared/utilities/utilites';
import { Subject, takeUntil, tap, timer } from 'rxjs';
import { FlightSearchStore } from '@calendar/flight-search/flight-search.store';
import { PinnedHelpComponent } from '@shared/components/pinned-help/pinned-help.component';

@Component({
  selector: 'app-calendar-card',
  templateUrl: './calendar-card.component.html',
  styleUrls: ['./calendar-card.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalendarCardComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('calendar') calendar?: FullCalendarComponent;

  calendar$ = this.calendarStore.calendar$;
  showDfps$ = this.calendarStore.showDfps$;
  destroy$ = new Subject<void>();
  specifiedEmpNum = '';
  isHolidayPayMonth = false;

  savedFlights$ = this.flightSearchStore.savedFlights$;

  // For referencing in monthly totals data box.
  monthlyTotalsInfoDialog = MonthlyTotalsInfoDialogComponent;

  calendarOptions: CalendarOptions = {
    plugins: [dayGridPlugin, interactionPlugin],
    height: 'auto',
    handleWindowResize: true,
    unselectAuto: false,
    fixedWeekCount: false,
    showNonCurrentDates: true,
    titleFormat: {
      month: 'long',
      year: 'numeric',
    },
    headerToolbar: {
      start: 'prev',
      center: 'title',
      end: 'next',
    },
    datesSet: this.onMonthChanged.bind(this),
    dateClick: this.onDateClick.bind(this),
    eventClick: this.onEventClick.bind(this),
  };

  defaultMonthlyTotals = [
    { label: 'PROJ', value: '00:00' },
    { label: 'PPROJ', value: '00:00' },
    { label: 'GTD', value: '00:00' },
    { label: 'IMAX', value: '00:00' },
  ];

  constructor(
    private calendarStore: CalendarStore,
    private hiTextStore: HiTextStore,
    private flightSearchStore: FlightSearchStore,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    // Get employee number from path for admin mode.
    this.specifiedEmpNum =
      this.route.snapshot.paramMap.get('employeeNumber') || '';

    // Set an initial date. This reloads a previously selected month if it
    // exists and and was set recently.
    const initialDate = this.calendarStore.getInitialDate();
    this.calendarStore.setSelectedMonth(initialDate);
    this.calendarOptions = {
      ...this.calendarOptions,
      initialDate,
    };
  }

  ngAfterViewInit(): void {
    // This is a temp fix for a know Full Calendar bug. It's supposed to be fixed in version 7.
    // Fixes issue where calendar width is incorrect. This calls updateSize() at regular intervals.
    timer(500, 2000)
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          if (this.calendar) {
            const api = this.calendar.getApi();
            api.updateSize();
          }
        }),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  onDfpToggleChanged(event: MatSlideToggleChange): void {
    this.calendarStore.setShowDfps(event.checked);
  }

  onRefreshClick(): void {
    this.calendarStore.forceRefresh();
  }

  /**
   * If sequence event clicked, navigate to sequence summary.
   * If non-sequence event clicked, show event description dialog.
   */
  // eslint-disable-next-line complexity
  onEventClick(event: EventClickArg): void {
    const { title } = event.event;
    const date = stripTime(event.event.extendedProps['startDate']);
    const seqPattern = /^\d{5}/;
    const sequenceNumber = event.event.extendedProps?.['sequenceNumber'];
    const position = event.event.extendedProps?.['position'];
    const isSeqDataMissing = event.event.extendedProps?.['isSeqDataMissing'];

    if (seqPattern.test(event.event.title)) {
      if (isSeqDataMissing) {
        this.dialog.open(ActivityDescriptionDialogComponent, {
          data: {
            code: title,
            description: 'Error: Sequence details missing',
          },
        });
      } else {
        this.router.navigate(['sequence', sequenceNumber, position, date]);
      }
    } else if (title.length === 2) {
      // Two chars indicate an add/remove code.
      let description = '';

      if (title === '24') {
        description = 'Regular 24 hour duty-free period';
      } else if (title === 'M2') {
        description = 'Movable day';
      } else if (title === 'G2') {
        description = 'Golden day';
      } else if (title === 'X2') {
        description = 'Movable day that has been moved';
      } else {
        description = REMOVE_CODES[title] || 'Unknown';
      }

      // Also provide sequence info.
      this.dialog.open(ActivityDescriptionDialogComponent, {
        data: {
          code: title,
          description,
          date,
          position,
          sequenceNumber,
        },
      });
    } else if (title.includes('RAP')) {
      const description = `Start: ${event.event.extendedProps?.['rapStart']} End: ${event.event.extendedProps?.['rapEnd']}`;

      this.dialog.open(ActivityDescriptionDialogComponent, {
        data: {
          code: 'RAP',
          description,
        },
      });
    }
  }

  onDateClick(): void {
    // Clear any selected sequence.
    this.router.navigate(['']);
  }

  onHI1Clicked(): void {
    this.openSabreDialog('hi1');
  }

  onHI2Clicked(): void {
    this.openSabreDialog('hi2');
  }

  openSabreDialog(type: 'hi1' | 'hi2'): void {
    this.dialog.open(SabreTextDialogComponent, {
      data: {
        sabreText$: this.hiTextStore.getActivity(type),
        refreshFn: this.hiTextStore.refreshFn,
        request: type,
      },
      panelClass: 'sabre-text',
      maxHeight: '95vh',
      maxWidth: '100vw',
    });
  }

  onPinnedHelpClicked(): void {
    this.dialog.open(PinnedHelpComponent, {
      maxHeight: '95vh',
      maxWidth: '100vw',
    });
  }

  // Updates selectedMonth observable triggering events update.
  onMonthChanged(dateRange: DatesSetArg): void {
    // dateRange just gives the start and end dates displayed on the calendar.
    // Add 15 to make sure the requested date is somewhere near the middle and
    // not in the wrong month.
    const date = DateTime.fromISO(dateRange.startStr).plus({ days: 15 });

    this.calendarStore.setSelectedMonth(date.toISODate());

    // Set isHolidayPayMonth flag.
    const month = date.month;
    if (
      month === 1 ||
      month === 5 ||
      month === 7 ||
      month === 11 ||
      month === 12
    ) {
      this.isHolidayPayMonth = true;
    } else {
      this.isHolidayPayMonth = false;
    }

    if (this.calendar) {
      const api = this.calendar.getApi();
      api.getEvents();
    }
  }
}
